import React from 'react';
import { Form, Input, Button, Card, Typography, message, Row, Col, Select, Checkbox, Divider } from 'antd';
import { LockOutlined, UserOutlined, MailOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useNavigate, Link } from 'react-router-dom';
import { authAPI, suppliersAPI } from '../services/api';

const { Title, Text } = Typography;

/** Принимает +7 (999) 123-45-67, 8999..., 999... и т.д. */
const validateRuMobile = (_: unknown, value: string) => {
  if (!value || !String(value).trim()) {
    return Promise.reject(new Error('Введите телефон'));
  }
  const digits = String(value).replace(/\D/g, '');
  let n = digits;
  if (n.length === 10 && n[0] === '9') {
    n = `7${n}`;
  } else if (n.length === 11 && n[0] === '8') {
    n = `7${n.slice(1)}`;
  }
  if (n.length !== 11 || n[0] !== '7' || n[1] !== '9') {
    return Promise.reject(new Error('Мобильный РФ: 10 цифр после +7, первая 9 (как в +7 999 123-45-67)'));
  }
  return Promise.resolve();
};

const Register: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const [role, setRole] = React.useState<'employee' | 'customer' | 'supplier' | 'delivery'>('employee');
  const [selectedSource, setSelectedSource] = React.useState<string>('');
  const [companyInfo, setCompanyInfo] = React.useState<any>(null);
  const [innLoading, setInnLoading] = React.useState(false);

  // Функция для определения правильных документов в зависимости от роли
  const getDocumentLinks = () => {
    switch (role) {
      case 'customer':
        return {
          terms: '/document/customer_terms',
          privacy: '/document/privacy'
        };
      case 'supplier':
        return {
          terms: '/document/supplier_terms',
          privacy: '/document/privacy'
        };
      case 'employee':
        return {
          terms: '/document/employee_terms',
          privacy: '/document/privacy'
        };
      case 'delivery':
        return {
          terms: '/document/terms', // Общий документ для delivery
          privacy: '/document/privacy'
        };
      default:
        return {
          terms: '/document/terms',
          privacy: '/document/privacy'
        };
    }
  };

  const getCompanyStatusText = (info: any) => {
    if (!info) return '';

    const status = info.approvalStatus || (info.isApproved ? 'approved' : 'pending');
    switch (status) {
      case 'approved':
        return 'Одобрена';
      case 'rejected':
        return 'Отклонена';
      default:
        return 'На рассмотрении';
    }
  };

  const getCompanyStatusColor = (info: any) => {
    if (!info) return '#595959';

    const status = info.approvalStatus || (info.isApproved ? 'approved' : 'pending');
    switch (status) {
      case 'approved':
        return '#52c41a';
      case 'rejected':
        return '#ff4d4f';
      default:
        return '#faad14';
    }
  };

  const onFinishFailed = (info: { errorFields?: { name: (string | number)[]; errors: string[] }[] }) => {
    const first = info?.errorFields?.[0];
    if (first?.errors?.length) {
      message.warning(first.errors[0]);
    } else {
      message.warning('Проверьте форму');
    }
  };

  const onFinish = async (values: any) => {
    if (!values.agree_terms || !values.agree_privacy) {
      message.error('Необходимо согласиться с соглашениями');
      return;
    }

    setLoading(true);
    try {
      const data: any = {
        email: values.email,
        username: values.username,
        password: values.password,
        role: role
      };

      if (role === 'employee') {
        data.inn = values.inn;
        data.fullName = values.full_name;
        data.phone = values.phone;
        data.email = values.email;
      }

      if (role === 'delivery') {
        data.inn = values.inn;
        data.contactName = values.contact_name;
        data.contactPhone = values.contact_phone;
        data.email = values.email;
      }

      if (role === 'supplier' || role === 'customer') {
        data.inn = values.inn;
        data.companyName = values.company_name;
        data.headPosition = values.head_position;
        data.headFullName = values.head_full_name;
        data.responsiblePosition = values.responsible_position;
        data.responsibleFullName = values.responsible_full_name;
        data.responsiblePhone = values.responsible_phone;
        data.responsibleEmail = values.responsible_email;
        data.email = values.email;
      }

      // Добавляем источник информации
      if (values.source) {
        // Если выбрано "Другое", используем кастомный текст
        data.source = values.source === 'other' ? values.customSource : values.source;
      }

      const result = await authAPI.register(data);

      if (result.pending) {
        message.success('Заявка отправлена на одобрение. После одобрения вы сможете войти.');
      } else {
        message.success('Регистрация успешна. Переход на страницу входа.');
      }
      navigate('/login', { replace: true });
    } catch (error: any) {
      const errMsg =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Ошибка регистрации';
      message.error(typeof errMsg === 'string' ? errMsg : 'Ошибка регистрации');
    } finally {
      setLoading(false);
    }
  };

  const validatePasswordsMatch = (_: any, value: string) => {
    const password = form.getFieldValue('password');
    if (value && value !== password) {
      return Promise.reject('Пароли не совпадают');
    }
    return Promise.resolve();
  };

  const handleInnChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const inn = e.target.value;
    
    // Проверяем формат ИНН
    if (inn && /^(\d{10}|\d{12})$/.test(inn)) {
      setInnLoading(true);
      try {
        const result = await suppliersAPI.lookupCompany(inn);
        setCompanyInfo(result);
      } catch (error) {
        console.error('Error looking up company:', error);
        setCompanyInfo({ found: false, message: 'Ошибка при поиске компании' });
      } finally {
        setInnLoading(false);
      }
    } else {
      setCompanyInfo(null);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#f0f2f5',
      padding: '10px'
    }}>
      <Row justify="center" style={{ width: '100%' }}>
        <Col xs={22} sm={18} md={14} lg={10} xl={8}>
          <Card
            style={{
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              borderRadius: '8px'
            }}
            variant="outlined"
          >
            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
              <Title level={3} style={{ margin: 0, color: '#1890ff' }}>
                🍽️ Регистрация
              </Title>
              <Text type="secondary" style={{ fontSize: '12px', marginTop: '4px', display: 'block' }}>
                Создайте новую учетную запись
              </Text>
            </div>

            <Form
              form={form}
              name="register"
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
              autoComplete="off"
              size="middle"
              layout="vertical"
            >
              <Form.Item label="Кого регистрируете?">
                <Select
                  value={role}
                  onChange={(v) => {
                    setRole(v);
                    setCompanyInfo(null);
                  }}
                  placeholder="Выберите тип пользователя"
                  style={{ width: '100%' }}
                >
                  <Select.Option value="employee">Сотрудник</Select.Option>
                  <Select.Option value="customer">Заказчик</Select.Option>
                  <Select.Option value="supplier">Поставщик</Select.Option>
                  <Select.Option value="delivery">Доставщик</Select.Option>
                </Select>
              </Form.Item>

              {(role === 'delivery' || role === 'employee') && (
                <Form.Item
                  label="ИНН организации"
                  name="inn"
                  preserve={false}
                  rules={[
                    { required: true, message: 'Введите ИНН' },
                    { pattern: /^(\d{10}|\d{12})$/, message: 'ИНН: 10 или 12 цифр' }
                  ]}
                >
                  <Input 
                    placeholder="10 или 12 цифр" 
                    onChange={handleInnChange}
                    suffix={innLoading ? <span>⏳</span> : undefined}
                  />
                </Form.Item>
              )}

              {(role === 'supplier' || role === 'customer') && (
                <Form.Item
                  label="ИНН организации"
                  name="inn"
                  preserve={false}
                  rules={[
                    { required: true, message: 'Введите ИНН' },
                    { pattern: /^(\d{10}|\d{12})$/, message: 'ИНН: 10 или 12 цифр' }
                  ]}
                >
                  <Input placeholder="10 или 12 цифр" />
                </Form.Item>
              )}

              {/* Отображение информации о компании */}
              {companyInfo && (role === 'delivery' || role === 'employee') && (
                <div style={{ 
                  marginBottom: '12px', 
                  padding: '8px', 
                  border: '1px solid #d9d9d9', 
                  borderRadius: '4px',
                  backgroundColor: companyInfo.found ? '#f6ffed' : '#fff2f0'
                }}>
                  {companyInfo.found ? (
                    <div>
                      <Text strong style={{ color: '#52c41a' }}>
                        ✅ Найдена компания:
                      </Text>
                      <br />
                      <Text strong>{companyInfo.companyName}</Text>
                      <br />
                      <Text type="secondary">
                        ИНН: {companyInfo.inn} | 
                        Тип: {companyInfo.type === 'supplier' ? 'Поставщик' : 'Заказчик'} |
                        <span style={{ color: getCompanyStatusColor(companyInfo) }}>
                          {' '}Статус: {getCompanyStatusText(companyInfo)}
                        </span>
                      </Text>
                    </div>
                  ) : (
                    <div>
                      <Text type="danger">
                        ❌ {companyInfo.message}
                      </Text>
                    </div>
                  )}
                </div>
              )}

              {role === 'employee' && (
                <>
                  <Form.Item
                    label="ФИО"
                    name="full_name"
                    preserve={false}
                    rules={[{ required: true, message: 'Введите ФИО' }]}
                  >
                    <Input placeholder="Иванов Иван Иванович" />
                  </Form.Item>

                  <Form.Item
                    label="Телефон"
                    name="phone"
                    preserve={false}
                    rules={[{ required: true, message: 'Введите телефон' }, { validator: validateRuMobile }]}
                  >
                    <Input placeholder="+7 (999) 123-45-67" />
                  </Form.Item>
                </>
              )}

              {role === 'delivery' && (
                <>
                  <Form.Item
                    label="Контактное лицо"
                    name="contact_name"
                    preserve={false}
                    rules={[{ required: true, message: 'Укажите контактное лицо' }]}
                  >
                    <Input placeholder="ФИО" />
                  </Form.Item>

                  <Form.Item
                    label="Телефон"
                    name="contact_phone"
                    preserve={false}
                    rules={[{ required: true, message: 'Укажите телефон' }, { validator: validateRuMobile }]}
                  >
                    <Input placeholder="+7 (999) 123-45-67" />
                  </Form.Item>
                </>
              )}

              {(role === 'supplier' || role === 'customer') && (
                <>
                  <Form.Item
                    label="Наименование организации"
                    name="company_name"
                    preserve={false}
                    rules={[{ required: true, message: 'Введите наименование организации' }]}
                  >
                    <Input placeholder="ООО 'Название компании'" />
                  </Form.Item>

                  <Divider orientation="left">Руководитель</Divider>
                  
                  <Form.Item
                    label="Должность руководителя"
                    name="head_position"
                    preserve={false}
                    rules={[{ required: true, message: 'Введите должность руководителя' }]}
                  >
                    <Input placeholder="Генеральный директор" />
                  </Form.Item>

                  <Form.Item
                    label="ФИО руководителя"
                    name="head_full_name"
                    preserve={false}
                    rules={[{ required: true, message: 'Введите ФИО руководителя' }]}
                  >
                    <Input placeholder="Иванов Иван Иванович" />
                  </Form.Item>

                  <Divider orientation="left">Ответственный сотрудник</Divider>

                  <Form.Item
                    label="Должность"
                    name="responsible_position"
                    preserve={false}
                    rules={[{ required: true, message: 'Введите должность' }]}
                  >
                    <Input placeholder="Менеджер" />
                  </Form.Item>

                  <Form.Item
                    label="ФИО"
                    name="responsible_full_name"
                    preserve={false}
                    rules={[{ required: true, message: 'Введите ФИО' }]}
                  >
                    <Input placeholder="Петров Петр Петрович" />
                  </Form.Item>

                  <Form.Item
                    label="Телефон"
                    name="responsible_phone"
                    preserve={false}
                    rules={[{ required: true, message: 'Введите телефон' }, { validator: validateRuMobile }]}
                  >
                    <Input placeholder="+7 (999) 123-45-67" />
                  </Form.Item>

                  <Form.Item
                    label="Email ответственного сотрудника"
                    name="responsible_email"
                    preserve={false}
                    rules={[
                      { required: true, message: 'Введите email' },
                      { type: 'email', message: 'Введите корректный email' }
                    ]}
                  >
                    <Input placeholder="manager@company.com" />
                  </Form.Item>
                </>
              )}

              <Form.Item
                label="Откуда вы о нас узнали?"
                name="source"
                rules={[{ required: true, message: 'Укажите источник информации' }]}
                style={{ marginBottom: '12px' }}
              >
                <Select 
                  placeholder="Выберите источник" 
                  allowClear
                  onChange={(value) => setSelectedSource(value)}
                >
                  <Select.Option value="google">Google поиск</Select.Option>
                  <Select.Option value="yandex">Яндекс поиск</Select.Option>
                  <Select.Option value="social_media">Социальные сети</Select.Option>
                  <Select.Option value="recommendation">Рекомендация знакомых</Select.Option>
                  <Select.Option value="employer">От работодателя</Select.Option>
                  <Select.Option value="advertisement">Реклама</Select.Option>
                  <Select.Option value="website">Сайт компании</Select.Option>
                  <Select.Option value="exhibition">Выставка/мероприятие</Select.Option>
                  <Select.Option value="other">Другое</Select.Option>
                </Select>
              </Form.Item>

              {selectedSource === 'other' && (
                <Form.Item
                  label="Укажите свой источник"
                  name="customSource"
                  preserve={false}
                  rules={[{ required: true, message: 'Введите источник информации' }]}
                >
                  <Input placeholder="Например: коллега, реклама в метро, рекомендация друга..." />
                </Form.Item>
              )}

              {role === 'employee' && (
                <Form.Item
                  label="Email (для входа в систему)"
                  name="email"
                  preserve={false}
                  rules={[
                    { required: true, message: 'Введите email!' },
                    { type: 'email', message: 'Некорректный email!' }
                  ]}
                >
                  <Input
                    prefix={<MailOutlined />}
                    placeholder="your@email.com"
                    autoComplete="off"
                  />
                </Form.Item>
              )}

              {(role === 'delivery' || role === 'supplier' || role === 'customer') && (
                <Form.Item
                  label="Email (для входа в систему)"
                  name="email"
                  preserve={false}
                  rules={[
                    { required: true, message: 'Введите email!' },
                    { type: 'email', message: 'Некорректный email!' }
                  ]}
                >
                  <Input
                    prefix={<MailOutlined />}
                    placeholder="your@email.com"
                    autoComplete="off"
                  />
                </Form.Item>
              )}

              <Form.Item
                label="Имя пользователя"
                name="username"
                rules={[
                  { required: true, message: 'Введите имя пользователя!' },
                  { min: 3, message: 'Минимум 3 символа!' },
                  { pattern: /^[a-zA-Z0-9_]+$/, message: 'Только буквы, цифры и подчеркивание!' }
                ]}
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder="username"
                  autoComplete="off"
                />
              </Form.Item>

              <Form.Item
                label="Пароль"
                name="password"
                rules={[
                  { required: true, message: 'Введите пароль!' },
                  { min: 6, message: 'Минимум 6 символов!' }
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Пароль"
                />
              </Form.Item>

              <Form.Item
                label="Повторите пароль"
                name="confirm_password"
                rules={[
                  { required: true, message: 'Повторите пароль!' },
                  { validator: validatePasswordsMatch }
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Повтор пароля"
                />
              </Form.Item>

              <Divider style={{ margin: '16px 0' }} />

              <Form.Item
                name="agree_terms"
                valuePropName="checked"
                initialValue={false}
                rules={[
                  {
                    validator: (_, v) =>
                      v === true ? Promise.resolve() : Promise.reject(new Error('Необходимо согласие с пользовательским соглашением')),
                  },
                ]}
                style={{ marginBottom: '8px' }}
              >
                <Checkbox>
                  <span>Я принимаю </span>
                  <a href={getDocumentLinks().terms} target="_blank" rel="noreferrer">
                    пользовательское соглашение
                  </a>
                </Checkbox>
              </Form.Item>

              <Form.Item
                name="agree_privacy"
                valuePropName="checked"
                initialValue={false}
                rules={[
                  {
                    validator: (_, v) =>
                      v === true ? Promise.resolve() : Promise.reject(new Error('Необходимо согласие с политикой конфиденциальности')),
                  },
                ]}
                style={{ marginBottom: '8px' }}
              >
                <Checkbox>
                  <span>Я согласен с </span>
                  <a href={getDocumentLinks().privacy} target="_blank" rel="noreferrer">
                    политикой конфиденциальности
                  </a>
                </Checkbox>
              </Form.Item>

              <Form.Item style={{ marginBottom: '8px' }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  style={{ width: '100%', height: '32px' }}
                >
                  Зарегистрироваться
                </Button>
              </Form.Item>
            </Form>

            <Divider style={{ margin: '12px 0' }} />

            <div style={{ textAlign: 'center' }}>
              <Text type="secondary">
                Уже есть аккаунт?{' '}
                <Link to="/login" style={{ color: '#1890ff', fontWeight: 500 }}>
                  Войти
                </Link>
              </Text>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Register;
